'use client'
import { ReviewAmenityGroup } from '@/src/types/hotels/details'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Dialog, Drawer, IconButton, Typography } from '@mui/material'
import { useState } from 'react'

export interface RoomAmenitiesProps {
    variant: 'mobile' | 'desktop'
    groups: ReviewAmenityGroup[]
    /** How many amenity items to show in the 2-col preview. */
    previewCount: number
    viewAllLabel: string
    /** Title of the view-all sheet/dialog. */
    title: string
}

// Room amenities: 2-col preview grid + a "view all" that opens a bottom Drawer on mobile
// and a centered Dialog on desktop. UI-only state.
const RoomAmenities: React.FC<RoomAmenitiesProps> = ({
    variant,
    groups,
    previewCount,
    viewAllLabel,
    title
}) => {
    const [open, setOpen] = useState(false)
    const flat = groups.flatMap(group => group.items)
    if (flat.length === 0) return null

    const preview = flat.slice(0, previewCount)
    // The desktop dialog is wide enough for two amenity columns; the mobile drawer stays single.
    const listColumns = variant === 'desktop' ? '1fr 1fr' : '1fr'

    const fullList = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groups.map(group => (
                <Box key={group.title}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: 'textColors.primary', mb: 0.75 }}>
                        {group.title}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: listColumns, columnGap: 4, rowGap: 1 }}>
                        {group.items.map(item => (
                            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: 'primary.main', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: 13, color: 'textColors.secondary' }}>{item}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}
        </Box>
    )

    const sheetHeader = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'borders.default',
                px: 2,
                py: 1.5
            }}
        >
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'textColors.primary' }}>{title}</Typography>
            <IconButton onClick={() => setOpen(false)} aria-label="Close" size="small">
                <CloseIcon />
            </IconButton>
        </Box>
    )

    return (
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {preview.map(item => (
                    <Box
                        key={item}
                        sx={{ width: '50%', display: 'flex', alignItems: 'center', gap: 1, py: 0.5, minWidth: 0 }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                        <Typography
                            sx={{ fontSize: 13, color: 'textColors.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {item}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box
                component="button"
                type="button"
                onClick={() => setOpen(true)}
                sx={{ border: 0, bgcolor: 'transparent', p: 0, mt: 0.5, cursor: 'pointer' }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'link.main' }}>{viewAllLabel}</Typography>
            </Box>

            {variant === 'mobile' ? (
                <Drawer
                    anchor="bottom"
                    open={open}
                    onClose={() => setOpen(false)}
                    PaperProps={{
                        sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '70dvh' }
                    }}
                >
                    {sheetHeader}
                    <Box sx={{ p: 2, overflowY: 'auto' }}>{fullList}</Box>
                </Drawer>
            ) : (
                <Dialog
                    open={open}
                    onClose={() => setOpen(false)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { maxHeight: '50vh' } }}
                >
                    {sheetHeader}
                    <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto' }}>{fullList}</Box>
                </Dialog>
            )}
        </Box>
    )
}

export { RoomAmenities }
export default RoomAmenities
